import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendar,
    faTimes,
    faSave,
    faSpinner,
    faTrash,
    faVideo,
    faVolumeUp,
    faFilePdf,
    faTag,
    faGlobe,
    faFolderOpen,
    faImage,
    faMicrophone,
    faShare,
    faCopy,
    faRotate,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faXTwitter, faFacebookSquare } from '@fortawesome/free-brands-svg-icons';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { eventsService } from '../../services/eventsService';
import { galleryService } from '../../services/galleryService';
import apiHelper from '../../utils/apiHelper';
import GalleryBrowser from '../Galleries/GalleryBrowser';
import DocumentBrowser from '../Repository/DocumentBrowser';
import './EventForm.css';

// Configure Quill for Agenda rich text field
const Quill = ReactQuill.Quill;

// Use style-based attributors for fonts and sizes
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['10pt', '10.5pt', '12pt', '14pt', '18pt'];
Quill.register(SizeStyle, true);

const FontStyle = Quill.import('attributors/style/font');
FontStyle.whitelist = ['arial', 'times-new-roman', 'calibri', 'helvetica', 'georgia', 'verdana'];
Quill.register(FontStyle, true);

// Configure Quill modules for agenda editing - basic tools only
const quillModules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }]
    ],
    clipboard: {
        matchVisual: false
    }
};

const quillFormats = [
    'bold', 'italic', 'underline',
    'list', 'bullet'
];

function EventForm({ event, onSave, onClose, currentUser, choiceFields: propChoiceFields, onChoiceFieldsRefresh }) {
    // Form state
    const [formData, setFormData] = useState({
        ID: '',
        Title: '',
        EventDescription_x002f_Narrative: '',
        EventDate: '',
        StartTime: '',
        EndTime: '',
        EventLocation: '',
        EventCategory: '',
        InPerson_x002f_Virtual: '',
        Speakers: '',
        Agenda: '',
        Topics: [],
        Sponsors: '',
        RegistrationLink: '',
        AgendaLink: '',
        ImageLink: '',
        VideoCollateral: '',
        AudioCollateral: '',
        FileCollateral: '',
        CollateralPublishFlag: false,
        PublishFlag: true,
        EventMonth: '',
        // Additional fields for storing selected media details
        imageName: '',
        videoName: '',
        audioName: '',
        documentName: '',
        // Blob URLs for media preview
        imageBlob: null,
        videoBlob: null,
        audioBlob: null,
        documentBlob: null,
        // Additional fields
        Created: '',
        Modified: '',
        Author: currentUser?.name || 'Admin',
        // Social media content fields
        SocialMediaPostLinkedIn: '',
        SocialMediaPostTwitterX: '',
        SocialMediaPostFacebook: ''
    });

    const [isLoading, setIsLoading] = useState(false);
    const [uploading, setUploading] = useState({
        video: false,
        audio: false,
        document: false,
        image: false
    });

    // Gallery and Document browser states
    const [galleries, setGalleries] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [loadingGalleries, setLoadingGalleries] = useState(false);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [showBrowser, setShowBrowser] = useState({
        image: false,
        video: false,
        audio: false,
        document: false
    });

    // Choice fields state (dynamic from SharePoint)
    const [choiceFields, setChoiceFields] = useState({
        categories: [],
        eventTypes: [],
        locations: []
    });
    const [isLoadingChoiceFields, setIsLoadingChoiceFields] = useState(true);

    // Custom field add states (like Media Centre components)
    const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
    const [customCategoryName, setCustomCategoryName] = useState('');
    const [isAddingNewEventType, setIsAddingNewEventType] = useState(false);
    const [customEventTypeName, setCustomEventTypeName] = useState('');
    const [isAddingNewLocation, setIsAddingNewLocation] = useState(false);
    const [customLocationName, setCustomLocationName] = useState('');

    // Social media content states (like ArticleForm)
    const [showSocialModal, setShowSocialModal] = useState(false);
    const [selectedPlatform, setSelectedPlatform] = useState('linkedin');
    const [socialContent, setSocialContent] = useState('');
    const [generatingPlatform, setGeneratingPlatform] = useState(null);
    const [clearedPosts, setClearedPosts] = useState(new Set());

    // Load choice fields when component mounts or use props
    useEffect(() => {
        if (propChoiceFields) {
            // Use choice fields from parent component
            setChoiceFields(propChoiceFields);
            setIsLoadingChoiceFields(false);
        } else {
            // Load choice fields directly if not provided by parent
            loadChoiceFields();
        }

        // Load galleries and documents
        loadGalleries();
        loadDocuments();
    }, [propChoiceFields]);

    const loadGalleries = async () => {
        setLoadingGalleries(true);
        try {
            const galleriesData = await galleryService.getGalleries();
            setGalleries(galleriesData || []);
        } catch (error) {
            console.error('Error loading galleries:', error);
            toast.error('Failed to load galleries');
        } finally {
            setLoadingGalleries(false);
        }
    };

    const loadDocuments = async () => {
        setLoadingDocuments(true);
        try {
            // TODO: Implement document loading from repository service
            // For now, we'll provide an empty array and show a message to users
       
            setDocuments([]);
        } catch (error) {
            console.error('Error loading documents:', error);
            toast.error('Failed to load documents');
        } finally {
            setLoadingDocuments(false);
        }
    };

    const loadChoiceFields = async () => {
        try {
            setIsLoadingChoiceFields(true);
            
            const fields = await eventsService.getChoiceFields();
            setChoiceFields(fields);
        } catch (error) {
            console.error('Failed to load event choice fields:', error);
            toast.warning('Some form options may not be available. Using defaults.');
            
            // Fallback to static options if API fails
            setChoiceFields({
                categories: [
                    'Webinar',
                    'Conference', 
                    'Workshop',
                    'Networking',
                    'Training',
                    'Panel Discussion',
                    'Launch Event',
                    'Annual Meeting',
                    'Committee Meeting',
                    'Other'
                ],
                eventTypes: [
                    'Virtual',
                    'In-Person',
                    'Hybrid'
                ],
                locations: []
            });
        } finally {
            setIsLoadingChoiceFields(false);
        }
    };

    useEffect(() => {
        if (event) {
            // Editing existing event
            const formattedDate = event.EventDate ? formatDateForInput(event.EventDate) : '';
            
            // Auto-generate EventMonth from EventDate if not already set
            let eventMonth = event.EventMonth;
            if (!eventMonth && formattedDate) {
                const date = new Date(formattedDate);
                const monthNames = [
                    'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'
                ];
                eventMonth = monthNames[date.getMonth()];
            }
            
            // Helper function to extract filename from full API URL for display
            const extractFilename = (urlOrFilename) => {
                if (!urlOrFilename) return '';
                if (urlOrFilename.includes('/getMediaContent/')) {
                    // Extract filename from full API URL like "https://api.../getMediaContent/filename.jpg"
                    return urlOrFilename.split('/getMediaContent/')[1];
                }
                if (urlOrFilename.includes('/getRepositoryFile/')) {
                    // Extract filename from repository file URL like "https://api.../getRepositoryFile/.../filename.pdf"
                    return urlOrFilename.split('/').pop(); // Get the last part of the URL
                }
                if (urlOrFilename.includes('sharepoint.com') && urlOrFilename.includes('/')) {
                    // Extract filename from SharePoint URL like "https://ifia.sharepoint.com/.../filename.pdf"
                    const extracted = urlOrFilename.split('/').pop().split('?')[0]; // Get filename, remove query params
                    // Decode URL encoding (like %20 for spaces)
                    return decodeURIComponent(extracted);
                }
                // If it's already just a filename or regular URL, return as-is
                return urlOrFilename;
            };

            const cleanedImageLink = extractFilename(event.ImageLink);
            const cleanedVideoCollateral = extractFilename(event.VideoCollateral);
            const cleanedAudioCollateral = extractFilename(event.AudioCollateral);
            const cleanedFileCollateral = extractFilename(event.FileCollateral);

            setFormData({
                ...event,
                EventDate: formattedDate,
                EventMonth: eventMonth || '',
                Topics: Array.isArray(event.Topics) ? event.Topics : 
                    (typeof event.Topics === 'string' ? 
                        event.Topics.split(',').map(t => t.trim()).filter(t => t) : []),
                // Clean up media URLs to store only filenames (fix existing full URLs)
                ImageLink: cleanedImageLink,
                VideoCollateral: cleanedVideoCollateral,
                AudioCollateral: cleanedAudioCollateral,
                FileCollateral: cleanedFileCollateral,
                // Initialize media name fields with cleaned data
                imageName: cleanedImageLink,
                videoName: cleanedVideoCollateral,
                audioName: cleanedAudioCollateral,
                documentName: cleanedFileCollateral,
                // Initialize social media fields with existing data
                SocialMediaPostLinkedIn: event.SocialMediaPostLinkedIn || '',
                SocialMediaPostTwitterX: event.SocialMediaPostTwitterX || '',
                SocialMediaPostFacebook: event.SocialMediaPostFacebook || ''
            });
            
            // Load existing image as blob for preview (use cleaned filename)
            if (cleanedImageLink) {
                loadExistingImage(cleanedImageLink);
            }
        } else {
            // Creating new event
            const now = new Date();
            setFormData(prev => ({
                ...prev,
                Created: now.toISOString(),
                Modified: now.toISOString()
            }));
        }
    }, [event]);

    // Function to load existing images via API (like handleMediaSelect does)
    const loadExistingImage = async (fileName) => {
        try {
            const headers = await apiHelper.getHeaders('graph/getMediaContent');
            const imageUrl = `${process.env.REACT_APP_API_URL}/api/graph/getMediaContent/${fileName}`;
            
            const response = await fetch(imageUrl, { headers });

            if (response.ok) {
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                
                setFormData(prev => ({
                    ...prev,
                    imageBlob: blobUrl
                }));
            }
        } catch (error) {
            // Silent fail - image preview not available for this file
        }
    };

    // Cleanup blob URLs when component unmounts
    useEffect(() => {
        return () => {
            // Clean up any blob URLs when component unmounts
            const blobFields = ['imageBlob', 'videoBlob', 'audioBlob', 'documentBlob'];
            blobFields.forEach(field => {
                if (formData[field]) {
                    URL.revokeObjectURL(formData[field]);
                }
            });
        };
    }, [formData]);

    const formatDateForInput = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().split('T')[0];
    };

    const formatDateForSocial = (date) => {
        if (!date) return 'TBC';
        const d = new Date(date);
        return d.toLocaleDateString('en-IE', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getSocialPostStatus = (platform) => {
        // If the post has been cleared locally, show nothing
        if (clearedPosts.has(platform)) {
            return '';
        }
        
        // Check current form data first
        const currentContent = formData[`SocialMediaPost${platform === 'twitter' ? 'TwitterX' : platform.charAt(0).toUpperCase() + platform.slice(1)}`];
        
        // If there's current content that's different from original, it's a draft
        if (currentContent) {
            const originalContent = event?.[`SocialMediaPost${platform === 'twitter' ? 'TwitterX' : platform.charAt(0).toUpperCase() + platform.slice(1)}`];
            if (originalContent && originalContent === currentContent) {
                return '(Saved)';
            }
            return '(Draft)';
        }
        
        return '';
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Handle dropdown changes for fill-in choice fields (like Media Centre)
        if (name === 'EventCategory') {
            if (value === 'ADD_NEW_CATEGORY') {
                setIsAddingNewCategory(true);
                setFormData(prev => ({
                    ...prev,
                    [name]: customCategoryName
                }));
            } else {
                setIsAddingNewCategory(false);
                setCustomCategoryName('');
                setFormData(prev => ({
                    ...prev,
                    [name]: value,
                    Modified: new Date().toISOString()
                }));
            }
        } else if (name === 'InPerson_x002f_Virtual') {
            if (value === 'ADD_NEW_EVENT_TYPE') {
                setIsAddingNewEventType(true);
                setFormData(prev => ({
                    ...prev,
                    [name]: customEventTypeName
                }));
            } else {
                setIsAddingNewEventType(false);
                setCustomEventTypeName('');
                setFormData(prev => ({
                    ...prev,
                    [name]: value,
                    Modified: new Date().toISOString()
                }));
            }
        } else if (name === 'EventLocation') {
            if (value === 'ADD_NEW_LOCATION') {
                setIsAddingNewLocation(true);
                setFormData(prev => ({
                    ...prev,
                    [name]: customLocationName
                }));
            } else {
                setIsAddingNewLocation(false);
                setCustomLocationName('');
                setFormData(prev => ({
                    ...prev,
                    [name]: value,
                    Modified: new Date().toISOString()
                }));
            }
        } else {
            const updates = {
                [name]: type === 'checkbox' ? checked : value,
                Modified: new Date().toISOString()
            };
            
            // Auto-populate EventMonth when EventDate changes
            if (name === 'EventDate' && value) {
                // Convert date to month name (YYYY-MM-DD -> "January", "February", etc.)
                const date = new Date(value);
                const monthNames = [
                    'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'
                ];
                updates.EventMonth = monthNames[date.getMonth()];
            }
            
            setFormData(prev => ({
                ...prev,
                ...updates
            }));
        }
    };

    // Custom field handlers (like Media Centre)
    const handleCustomCategoryChange = (e) => {
        const value = e.target.value;
        setCustomCategoryName(value);
        setFormData(prev => ({
            ...prev,
            EventCategory: value
        }));
    };

    const handleCustomEventTypeChange = (e) => {
        const value = e.target.value;
        setCustomEventTypeName(value);
        setFormData(prev => ({
            ...prev,
            InPerson_x002f_Virtual: value
        }));
    };

    const handleCustomLocationChange = (e) => {
        const value = e.target.value;
        setCustomLocationName(value);
        setFormData(prev => ({
            ...prev,
            EventLocation: value
        }));
    };

    // Back to dropdown handlers (like Media Centre)
    const handleBackToCategoryDropdown = () => {
        setIsAddingNewCategory(false);
        setCustomCategoryName('');
        setFormData(prev => ({
            ...prev,
            EventCategory: ''
        }));
    };

    const handleBackToEventTypeDropdown = () => {
        setIsAddingNewEventType(false);
        setCustomEventTypeName('');
        setFormData(prev => ({
            ...prev,
            InPerson_x002f_Virtual: ''
        }));
    };

    const handleBackToLocationDropdown = () => {
        setIsAddingNewLocation(false);
        setCustomLocationName('');
        setFormData(prev => ({
            ...prev,
            EventLocation: ''
        }));
    };

    const handleTopicSelect = (e) => {
        const selectedTopic = e.target.value;
        if (selectedTopic && !formData.Topics.includes(selectedTopic)) {
            setFormData(prev => ({
                ...prev,
                Topics: [...prev.Topics, selectedTopic]
            }));
        }
        // Reset the select to placeholder
        e.target.value = '';
    };

    const handleTopicRemove = (topicToRemove) => {
        setFormData(prev => ({
            ...prev,
            Topics: prev.Topics.filter(topic => topic !== topicToRemove)
        }));
    };

    const handleAgendaChange = (content) => {
        setFormData(prev => ({
            ...prev,
            Agenda: content,
            Modified: new Date().toISOString()
        }));
    };

    const handleFileUpload = async (file, type) => {
        if (!file) return;

        setUploading(prev => ({ ...prev, [type]: true }));
        
        try {
            // TODO: Implement actual file upload to your storage system
            // For now, we'll simulate the upload
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            const mockUrl = `https://example.com/uploads/${type}/${file.name}`;
            
            switch (type) {
                case 'video':
                    setFormData(prev => ({ ...prev, VideoCollateral: mockUrl }));
                    break;
                case 'audio':
                    setFormData(prev => ({ ...prev, AudioCollateral: mockUrl }));
                    break;
                case 'document':
                    setFormData(prev => ({ ...prev, FileCollateral: mockUrl }));
                    break;
                case 'image':
                    setFormData(prev => ({ ...prev, ImageLink: mockUrl }));
                    break;
            }
            
            toast.success(`${type} uploaded successfully`);
        } catch (error) {
            console.error(`Error uploading ${type}:`, error);
            toast.error(`Failed to upload ${type}`);
        } finally {
            setUploading(prev => ({ ...prev, [type]: false }));
        }
    };

    const handleRemoveFile = (type) => {
        const fieldMap = {
            video: 'VideoCollateral',
            audio: 'AudioCollateral',
            document: 'FileCollateral',
            image: 'ImageLink'
        };

        const nameFieldMap = {
            video: 'videoName',
            audio: 'audioName',
            document: 'documentName',
            image: 'imageName'
        };

        const blobFieldMap = {
            video: 'videoBlob',
            audio: 'audioBlob',
            document: 'documentBlob',
            image: 'imageBlob'
        };

        setFormData(prev => {
            // Clean up blob URL if it exists
            if (prev[blobFieldMap[type]]) {
                URL.revokeObjectURL(prev[blobFieldMap[type]]);
            }

            return {
                ...prev,
                [fieldMap[type]]: '',
                [nameFieldMap[type]]: '',
                [blobFieldMap[type]]: null
            };
        });

        toast.success('File removed successfully');
    };

    // Browser control functions
    const handleBrowseClick = (type) => {
       
        
        if (type === 'image' || type === 'video' || type === 'audio') {
            if (galleries.length === 0) {
                loadGalleries();
            }
        } else if (type === 'document') {
            if (documents.length === 0) {
               
                loadDocuments();
            }
        }
        setShowBrowser(prev => ({ ...prev, [type]: true }));
       
    };

    const handleBrowserClose = (type) => {
        setShowBrowser(prev => ({ ...prev, [type]: false }));
    };

    const handleMediaSelect = async (type, item) => {
        const fieldMap = {
            video: 'VideoCollateral',
            audio: 'AudioCollateral',
            document: 'FileCollateral',
            image: 'ImageLink'
        };

        const nameFieldMap = {
            video: 'videoName',
            audio: 'audioName',
            document: 'documentName',
            image: 'imageName'
        };

        const blobFieldMap = {
            video: 'videoBlob',
            audio: 'audioBlob',
            document: 'documentBlob',
            image: 'imageBlob'
        };

        // Clean up previous blob URL if it exists
        setFormData(prev => {
            if (prev[blobFieldMap[type]]) {
                URL.revokeObjectURL(prev[blobFieldMap[type]]);
            }
            return prev;
        });

        // Determine the URL and create blob if needed
        let mediaUrl = '';
        let blobUrl = null;
        const mediaName = item.title || item.name || item.fileName || 'Selected file';

        if (item.url && item.url.includes('https://')) {
            // Check if it's a getMediaContent URL that we should extract filename from
            if (item.url.includes('/getMediaContent/')) {
                // Gallery media content - store just the filename like other gallery items
                const fileName = item.url.split('/getMediaContent/')[1];
                mediaUrl = fileName;
                
                if (type === 'image') {
                    // For images, create blob URL for preview
                    try {
                        const headers = await apiHelper.getHeaders('graph/getMediaContent');
                        const response = await fetch(item.url, { headers });
                        if (response.ok) {
                            const blob = await response.blob();
                            blobUrl = URL.createObjectURL(blob);
                        }
                    } catch (error) {
                        // Fallback to direct URL for preview
                        blobUrl = item.url;
                    }
                }
            } else if (item.url.includes('/getRepositoryFile/') && (item.fileName || item.name)) {
                // Repository document - store just the filename like gallery items
                const fileName = item.fileName || item.name;
                mediaUrl = fileName;
            } else {
                // External URL or CDN URL - use directly
                mediaUrl = item.url;
                if (type === 'image') {
                    blobUrl = item.url; // For images, use the URL directly for preview
                }
            }
        } else if (item.serverRelativeUrl && item.serverRelativeUrl.includes('https://')) {
            // SharePoint absolute URL - use directly  
            mediaUrl = item.serverRelativeUrl;
            if (type === 'image') {
                blobUrl = item.serverRelativeUrl;
            }
        } else if (item.file || item.blob) {
            // File object or blob - create blob URL for preview
            const fileData = item.file || item.blob;
            blobUrl = URL.createObjectURL(fileData);
            mediaUrl = item.name || item.fileName || 'Uploaded file';
        } else if (item.name || item.fileName) {
            // Gallery item name - store just the filename (like ArticleForm does)
            const fileName = item.name || item.fileName;
            mediaUrl = fileName; // Store only the filename, not the full API URL
            
            if (type === 'image') {
                // For images, create blob URL for preview only (not stored in database)
                try {
                    const headers = await apiHelper.getHeaders('graph/getMediaContent');
                    const imageUrl = `${process.env.REACT_APP_API_URL}/api/graph/getMediaContent/${fileName}`;
                    
                    const response = await fetch(imageUrl, { headers });

                    if (response.ok) {
                        const blob = await response.blob();
                        blobUrl = URL.createObjectURL(blob);
                    }
                } catch (error) {
                    // Silent fail - preview not available for this file
                }
            }
        }

        // Update form data with selected media
        setFormData(prev => ({
            ...prev,
            [fieldMap[type]]: mediaUrl,
            [nameFieldMap[type]]: mediaName,
            [blobFieldMap[type]]: blobUrl
        }));

        // Close browser
        handleBrowserClose(type);
        
        // Show success message
        const mediaTypeLabel = {
            image: 'Image',
            video: 'Video',
            audio: 'Audio',
            document: 'Document'
        }[type];
        
        toast.success(`${mediaTypeLabel} "${mediaName}" selected!`);
    };

    const validateForm = () => {
        if (!formData.Title.trim()) {
            toast.error('Event title is required');
            return false;
        }
        
        if (!formData.EventDate) {
            toast.error('Event date is required');
            return false;
        }
        
        return true;
    };

    // Social media generation functions (like ArticleForm)
    const handleGenerateSocial = async (platform) => {
        try {
            // Check if content already exists for this platform
            const existingContent = {
                linkedin: formData.SocialMediaPostLinkedIn,
                twitter: formData.SocialMediaPostTwitterX,
                facebook: formData.SocialMediaPostFacebook
            }[platform];

            if (existingContent) {
                // Show existing content without regenerating
                setSocialContent(existingContent);
                setSelectedPlatform(platform);
                setShowSocialModal(true);
                return;
            }

            // If no existing content, proceed with generation
            setGeneratingPlatform(platform);

            // Prepare comprehensive event data for AI generation
            const eventDataForAI = {
                title: formData.Title,
                content: formData.EventDescription_x002f_Narrative || '',
                eventDate: formData.EventDate,
                eventLocation: formData.EventLocation,
                eventCategory: formData.EventCategory,
                eventType: formData.InPerson_x002f_Virtual,
                speakers: formData.Speakers,
                agenda: formData.Agenda,
                startTime: formData.StartTime,
                endTime: formData.EndTime,
                registrationLink: formData.RegistrationLink,
                agendaLink: formData.AgendaLink,
                sponsors: formData.Sponsors,
                topics: formData.Topics?.join(', '),
                capacity: formData.Capacity,
                targetAudience: formData.TargetAudience,
                venue: {
                    location: formData.EventLocation,
                    type: formData.InPerson_x002f_Virtual,
                    address: formData.VenueAddress || '',
                    accessibility: formData.AccessibilityInfo || ''
                },
                timing: {
                    date: formatDateForSocial(formData.EventDate),
                    startTime: formData.StartTime,
                    endTime: formData.EndTime,
                    duration: formData.StartTime && formData.EndTime ? 
                        `${formData.StartTime} - ${formData.EndTime}` : 'TBC'
                },
                registration: {
                    link: formData.RegistrationLink,
                    deadline: formData.RegistrationDeadline || '',
                    fee: formData.RegistrationFee || 'Free'
                },
                additionalInfo: {
                    networkingOpportunities: formData.NetworkingFlag ? 'Networking opportunities available' : '',
                    cpdCredits: formData.CPDCredits || '',
                    materials: formData.EventMaterials || '',
                    requirements: formData.Requirements || ''
                }
            };

            const result = await apiHelper.generateSocialContent(eventDataForAI, platform);
            const content = result.data.socialContent || result.data;

            // Update formData with the generated content
            setFormData(prev => ({
                ...prev,
                ...(platform === 'linkedin' && { SocialMediaPostLinkedIn: content }),
                ...(platform === 'twitter' && { SocialMediaPostTwitterX: content }),
                ...(platform === 'facebook' && { SocialMediaPostFacebook: content })
            }));

            // Remove platform from cleared posts since we now have new content
            setClearedPosts(prev => {
                const newSet = new Set(prev);
                newSet.delete(platform);
                return newSet;
            });

            setSocialContent(content);
            setSelectedPlatform(platform);
            setShowSocialModal(true);

            toast.success('Social media content generated!');
        } catch (error) {
            console.error('Error generating social content:', error);
            toast.error('Failed to generate social content');
        } finally {
            setGeneratingPlatform(null);
        }
    };

    const handleRegenerateSocial = async () => {
        try {
            setGeneratingPlatform(selectedPlatform);

            // Prepare comprehensive event data for AI generation
            const eventDataForAI = {
                title: formData.Title,
                content: formData.EventDescription_x002f_Narrative || '',
                eventDate: formData.EventDate,
                eventLocation: formData.EventLocation,
                eventCategory: formData.EventCategory,
                eventType: formData.InPerson_x002f_Virtual,
                speakers: formData.Speakers,
                agenda: formData.Agenda,
                startTime: formData.StartTime,
                endTime: formData.EndTime,
                registrationLink: formData.RegistrationLink,
                agendaLink: formData.AgendaLink,
                sponsors: formData.Sponsors,
                topics: formData.Topics?.join(', '),
                capacity: formData.Capacity,
                targetAudience: formData.TargetAudience,
                venue: {
                    location: formData.EventLocation,
                    type: formData.InPerson_x002f_Virtual,
                    address: formData.VenueAddress || '',
                    accessibility: formData.AccessibilityInfo || ''
                },
                timing: {
                    date: formatDateForSocial(formData.EventDate),
                    startTime: formData.StartTime,
                    endTime: formData.EndTime,
                    duration: formData.StartTime && formData.EndTime ? 
                        `${formData.StartTime} - ${formData.EndTime}` : 'TBC'
                },
                registration: {
                    link: formData.RegistrationLink,
                    deadline: formData.RegistrationDeadline || '',
                    fee: formData.RegistrationFee || 'Free'
                },
                additionalInfo: {
                    networkingOpportunities: formData.NetworkingFlag ? 'Networking opportunities available' : '',
                    cpdCredits: formData.CPDCredits || '',
                    materials: formData.EventMaterials || '',
                    requirements: formData.Requirements || ''
                }
            };

            const result = await apiHelper.generateSocialContent(eventDataForAI, selectedPlatform);
            const content = result.data.socialContent || result.data;

            // Update formData with the regenerated content
            setFormData(prev => ({
                ...prev,
                ...(selectedPlatform === 'linkedin' && { SocialMediaPostLinkedIn: content }),
                ...(selectedPlatform === 'twitter' && { SocialMediaPostTwitterX: content }),
                ...(selectedPlatform === 'facebook' && { SocialMediaPostFacebook: content })
            }));

            // Remove platform from cleared posts since we now have new content
            setClearedPosts(prev => {
                const newSet = new Set(prev);
                newSet.delete(selectedPlatform);
                return newSet;
            });

            setSocialContent(content);
            toast.success('Social media content regenerated!');
        } catch (error) {
            console.error('Error regenerating social content:', error);
            toast.error('Failed to regenerate social content');
        } finally {
            setGeneratingPlatform(null);
        }
    };

    const handleCopySocial = (event) => {
        // Prevent any form submission or event bubbling
        event.preventDefault();
        event.stopPropagation();

        navigator.clipboard.writeText(socialContent);
        toast.success('Copied to clipboard!');
    };

    const handleClearSocial = (platform) => {
        // Check if there was originally saved content for this platform
        const fieldName = `SocialMediaPost${platform === 'twitter' ? 'TwitterX' : platform.charAt(0).toUpperCase() + platform.slice(1)}`;
        const hadOriginalContent = event && event[fieldName];

        // Clear the social media content for the specified platform
        setFormData(prev => ({
            ...prev,
            ...(platform === 'linkedin' && { SocialMediaPostLinkedIn: '' }),
            ...(platform === 'twitter' && { SocialMediaPostTwitterX: '' }),
            ...(platform === 'facebook' && { SocialMediaPostFacebook: '' })
        }));

        // Only track as "cleared" if there was originally saved content
        if (hadOriginalContent) {
            setClearedPosts(prev => new Set([...prev, platform]));
        }

        // Close modal if it's open for the platform being cleared
        if (selectedPlatform === platform && showSocialModal) {
            setShowSocialModal(false);
            setSocialContent('');
        }

        // Show appropriate message based on whether there was original content
        if (hadOriginalContent) {
            toast.success(`${platform.charAt(0).toUpperCase() + platform.slice(1)} post cleared! Click "Update Event" to save changes.`, {
                autoClose: 4000
            });
        } else {
            toast.success(`${platform.charAt(0).toUpperCase() + platform.slice(1)} post cleared!`);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        
        try {
            // Helper function to extract filename from full API URL
            const extractFilename = (urlOrFilename) => {
                if (!urlOrFilename) return '';
                
                if (urlOrFilename.includes('/getMediaContent/')) {
                    // Extract filename from full API URL like "https://api.../getMediaContent/filename.jpg"
                    return urlOrFilename.split('/getMediaContent/')[1];
                }
                if (urlOrFilename.includes('/getRepositoryFile/')) {
                    // Extract filename from repository file URL like "https://api.../getRepositoryFile/.../filename.pdf"
                    return urlOrFilename.split('/').pop(); // Get the last part of the URL
                }
                if (urlOrFilename.includes('sharepoint.com') && urlOrFilename.includes('/')) {
                    // Extract filename from SharePoint URL like "https://ifia.sharepoint.com/.../filename.pdf"
                    const extracted = urlOrFilename.split('/').pop().split('?')[0]; // Get filename, remove query params
                    // Decode URL encoding (like %20 for spaces)
                    return decodeURIComponent(extracted);
                }
                
                // If it's already just a filename or regular URL, return as-is
                return urlOrFilename;
            };

            const cleanedImageLink = extractFilename(formData.ImageLink);
            const cleanedVideoCollateral = extractFilename(formData.VideoCollateral);
            const cleanedAudioCollateral = extractFilename(formData.AudioCollateral);
            const cleanedFileCollateral = extractFilename(formData.FileCollateral);

            // Process the data before saving
            const eventData = {
                ...formData,
                // Only include ID if editing existing event (not creating new)
                ...(formData.ID ? { ID: formData.ID } : {}),
                EventDate: formData.EventDate ? new Date(formData.EventDate).toISOString() : null,
                Topics: formData.Topics.join(','), // Convert array back to comma-separated string
                CollateralPublishFlag: !!(formData.VideoCollateral || formData.AudioCollateral || formData.FileCollateral),
                // Clean up media URLs to store only filenames (not full API URLs)
                ImageLink: cleanedImageLink,
                VideoCollateral: cleanedVideoCollateral,
                AudioCollateral: cleanedAudioCollateral,
                FileCollateral: cleanedFileCollateral,
                // Include social media content (send null for cleared posts to properly clear in database)
                SocialMediaPostLinkedIn: clearedPosts.has('linkedin') ? null : (formData.SocialMediaPostLinkedIn || null),
                SocialMediaPostTwitterX: clearedPosts.has('twitter') ? null : (formData.SocialMediaPostTwitterX || null),
                SocialMediaPostFacebook: clearedPosts.has('facebook') ? null : (formData.SocialMediaPostFacebook || null)
            };

            await onSave(eventData);
            
            // Clear the cleared posts tracking since everything is now saved
            setClearedPosts(new Set());
            
            // Refresh choice fields after save in case new values were added
            if (onChoiceFieldsRefresh) {
                await onChoiceFieldsRefresh();
            } else {
                await loadChoiceFields();
            }
        } catch (error) {
            console.error('Error saving event:', error);
            // Error handling is done in the parent component
        } finally {
            setIsLoading(false);
        }
    };

    // Enhanced media input component that supports both URL and gallery/repository browsing
    const renderMediaInput = (type, currentFile, icon, label, placeholder) => {
        const fieldMap = {
            video: 'VideoCollateral',
            audio: 'AudioCollateral', 
            document: 'FileCollateral',
            image: 'ImageLink'
        };

        const nameFieldMap = {
            video: 'videoName',
            audio: 'audioName',
            document: 'documentName',
            image: 'imageName'
        };

        const blobFieldMap = {
            video: 'videoBlob',
            audio: 'audioBlob',
            document: 'documentBlob',
            image: 'imageBlob'
        };

        const isLoading = type === 'image' || type === 'video' || type === 'audio' ? loadingGalleries : loadingDocuments;
        
        // Determine what to display in the input
        const displayValue = formData[nameFieldMap[type]] || currentFile || '';
        const hasBlob = !!formData[blobFieldMap[type]];
        const hasFile = !!(currentFile || formData[nameFieldMap[type]]);

        return (
            <div className="media-input-section">
                <label className="media-input-label">
                    <FontAwesomeIcon icon={icon} /> {label}
                </label>
                
                <div className="media-input-container">
                    <input
                        type="text"
                        value={displayValue}
                        onChange={(e) => {
                            const newValue = e.target.value;
                            
                            setFormData(prev => {
                                // Clean up any existing blob URL if switching to manual input
                                if (prev[blobFieldMap[type]]) {
                                    URL.revokeObjectURL(prev[blobFieldMap[type]]);
                                }
                                
                                return {
                                    ...prev,
                                    [fieldMap[type]]: newValue,
                                    [nameFieldMap[type]]: newValue,
                                    // Clear blob URL since this is manual input
                                    [blobFieldMap[type]]: null
                                };
                            });
                        }}
                        placeholder={placeholder}
                        className={`media-url-input ${hasBlob ? 'has-blob' : ''}`}
                        title={hasBlob ? 'Selected from gallery' : 'Manual URL entry'}
                    />
                    
                    <div className="media-buttons">
                        <button
                            type="button"
                            className="btn-browse"
                            onClick={() => handleBrowseClick(type)}
                            disabled={isLoading}
                            title={type === 'document' ? 'Browse repository for PDF documents' : 'Browse gallery for media'}
                        >
                            {isLoading ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                            ) : (
                                <FontAwesomeIcon icon={faFolderOpen} />
                            )}
                        </button>
                        
                        {hasFile && (
                            <button
                                type="button"
                                className="btn-clear"
                                onClick={() => handleRemoveFile(type)}
                                title="Clear selection"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                    </div>
                </div>
                
                {/* Media input help/status - moved above image preview */}
                <div className="media-input-help">
                    {hasBlob && (
                        <span style={{ color: '#4caf50', fontWeight: 'bold' }}>
                            ✓ Selected from gallery • 
                        </span>
                    )}
                    {type === 'document' ? 
                        'Enter a URL or browse the repository for PDF documents' :
                        `Enter a ${type} URL or browse the gallery for uploaded ${type}s`
                    }
                </div>
                
                {/* Image preview - handles blob URLs and direct URLs */}
                {type === 'image' && (
                    <div className="media-preview-debug">
                        {/* Show preview for blob URLs (from gallery selection) OR direct URLs (existing events) */}
                        {(() => {
                            const blobUrl = formData[blobFieldMap[type]];
                            const existingImageBlob = formData.imageBlob;
                            
                            // Only show preview if we have a blob URL (never show filenames as images)
                            const shouldShowPreview = !!(blobUrl || existingImageBlob);
                            
                            return shouldShowPreview;
                        })() && (
                            <div className="media-preview">
                                <img 
                                    src={
                                        formData[blobFieldMap[type]] || // Only use blob URLs for preview
                                        formData.imageBlob // Fallback to existing image blob
                                    } 
                                    alt="Image preview" 
                                    style={{
                                        maxWidth: '200px',
                                        maxHeight: '150px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: hasBlob ? '2px solid #4caf50' : '2px solid #2196f3',
                                        marginTop: '0.5rem',
                                        display: 'block'
                                    }}
                                    onLoad={() => {
                                        // Image loaded successfully
                                    }}
                                    onError={() => {
                                        // Image failed to load
                                    }}
                                />
                            </div>
                        )}

                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="event-form-overlay">
            <div className="event-form-modal">
                <div className="event-form-header">
                    <h2>
                        <FontAwesomeIcon icon={faCalendar} />
                        {event ? 'Edit Event' : 'Create New Event'}
                    </h2>
                    <button className="btn-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <form className="event-form" onSubmit={handleSubmit}>
                    <div className="form-grid">
                        {/* Basic Information */}
                        <div className="form-section">
                            <h3>Basic Information</h3>
                            
                            <div className="form-group">
                                <label htmlFor="Title">Event Title *</label>
                                <input
                                    type="text"
                                    id="Title"
                                    name="Title"
                                    value={formData.Title}
                                    onChange={handleInputChange}
                                    placeholder="Enter event title"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="EventDescription_x002f_Narrative">Description</label>
                                <textarea
                                    id="EventDescription_x002f_Narrative"
                                    name="EventDescription_x002f_Narrative"
                                    value={formData.EventDescription_x002f_Narrative}
                                    onChange={handleInputChange}
                                    placeholder="Event description or narrative"
                                    rows={4}
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="EventCategory">Category</label>
                                    {isAddingNewCategory ? (
                                        <div className="custom-input-wrapper">
                                            <input
                                                type="text"
                                                value={customCategoryName}
                                                onChange={handleCustomCategoryChange}
                                                placeholder="Enter new category..."
                                                autoFocus
                                            />
                                            <button
                                                type="button"
                                                className="btn-back"
                                                onClick={handleBackToCategoryDropdown}
                                                title="Back to category list"
                                            >
                                                ↩️ Back
                                            </button>
                                        </div>
                                    ) : (
                                        <select
                                            id="EventCategory"
                                            name="EventCategory"
                                            value={formData.EventCategory}
                                            onChange={handleInputChange}
                                            disabled={isLoadingChoiceFields}
                                        >
                                            <option value="">
                                                {isLoadingChoiceFields ? 'Loading categories...' : 'Select Category'}
                                            </option>
                                            {choiceFields.categories?.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                            <option value="ADD_NEW_CATEGORY" style={{ borderTop: '1px solid #ccc', fontStyle: 'italic' }}>
                                                ➕ Add New Category...
                                            </option>
                                        </select>
                                    )}
                                    {isLoadingChoiceFields && (
                                        <div className="loading-indicator">
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            <span>Loading options...</span>
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="InPerson_x002f_Virtual">Event Type</label>
                                    {isAddingNewEventType ? (
                                        <div className="custom-input-wrapper">
                                            <input
                                                type="text"
                                                value={customEventTypeName}
                                                onChange={handleCustomEventTypeChange}
                                                placeholder="Enter new event type..."
                                                autoFocus
                                            />
                                            <button
                                                type="button"
                                                className="btn-back"
                                                onClick={handleBackToEventTypeDropdown}
                                                title="Back to event type list"
                                            >
                                                ↩️ Back
                                            </button>
                                        </div>
                                    ) : (
                                        <select
                                            id="InPerson_x002f_Virtual"
                                            name="InPerson_x002f_Virtual"
                                            value={formData.InPerson_x002f_Virtual}
                                            onChange={handleInputChange}
                                            disabled={isLoadingChoiceFields}
                                        >
                                            <option value="">
                                                {isLoadingChoiceFields ? 'Loading types...' : 'Select Type'}
                                            </option>
                                            {choiceFields.eventTypes?.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                            <option value="ADD_NEW_EVENT_TYPE" style={{ borderTop: '1px solid #ccc', fontStyle: 'italic' }}>
                                                ➕ Add New Type...
                                            </option>
                                        </select>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Date and Time */}
                        <div className="form-section">
                            <h3>Date & Time</h3>
                            
                            <div className="form-group">
                                <label htmlFor="EventDate">Event Date *</label>
                                <input
                                    type="date"
                                    id="EventDate"
                                    name="EventDate"
                                    value={formData.EventDate}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="StartTime">Start Time</label>
                                    <input
                                        type="time"
                                        id="StartTime"
                                        name="StartTime"
                                        value={formData.StartTime}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="EndTime">End Time</label>
                                    <input
                                        type="time"
                                        id="EndTime"
                                        name="EndTime"
                                        value={formData.EndTime}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="PublishFlag">Event Publication</label>
                                <div className="publish-toggle-container">
                                    <input
                                        type="checkbox"
                                        id="PublishFlag"
                                        name="PublishFlag"
                                        checked={formData.PublishFlag}
                                        onChange={handleInputChange}
                                        className="publish-switch"
                                    />
                                    <label htmlFor="PublishFlag" className="publish-switch-label">
                                        <span className="publish-switch-slider"></span>
                                    </label>
                                    <span className={`publish-status ${formData.PublishFlag ? 'published' : 'unpublished'}`}>
                                        {formData.PublishFlag ? (
                                            <>
                                                <FontAwesomeIcon icon={faGlobe} className="status-icon published" />
                                                Event visible to members
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faGlobe} className="status-icon unpublished" />
                                                Event hidden from members
                                            </>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Location and Details */}
                        <div className="form-section">
                            <h3>Location & Details</h3>
                            
                            <div className="form-group">
                                <label htmlFor="EventLocation">Location</label>
                                {isAddingNewLocation ? (
                                    <div className="custom-input-wrapper">
                                        <input
                                            type="text"
                                            value={customLocationName}
                                            onChange={handleCustomLocationChange}
                                            placeholder="Enter new location..."
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            className="btn-back"
                                            onClick={handleBackToLocationDropdown}
                                            title="Back to location list"
                                        >
                                            ↩️ Back
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            type="text"
                                            id="EventLocation"
                                            name="EventLocation"
                                            value={formData.EventLocation}
                                            onChange={handleInputChange}
                                            placeholder="Event location or virtual platform"
                                            list="location-suggestions"
                                        />
                                        {choiceFields.locations?.length > 0 && (
                                            <datalist id="location-suggestions">
                                                {choiceFields.locations.map(location => (
                                                    <option key={location} value={location} />
                                                ))}
                                                <option value="ADD_NEW_LOCATION">➕ Add New Location...</option>
                                            </datalist>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="Speakers">Speakers</label>
                                <textarea
                                    id="Speakers"
                                    name="Speakers"
                                    value={formData.Speakers}
                                    onChange={handleInputChange}
                                    placeholder="List of speakers (one per line)"
                                    rows={3}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="Agenda">Agenda</label>
                                <div className="rich-text-editor">
                                    <ReactQuill
                                        value={formData.Agenda || ''}
                                        onChange={handleAgendaChange}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        placeholder="Enter event agenda details..."
                                        style={{ height: '200px', marginBottom: '50px' }}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="Sponsors">Sponsors</label>
                                <textarea
                                    id="Sponsors"
                                    name="Sponsors"
                                    value={formData.Sponsors}
                                    onChange={handleInputChange}
                                    placeholder="Event sponsors (one per line)"
                                    rows={3}
                                />
                            </div>
                        </div>

                        {/* Topics */}
                        <div className="form-section">
                            <h3>Topics</h3>
                            
                            <div className="topics-input">
                                <div className="topic-select">
                                    <select onChange={handleTopicSelect} defaultValue="">
                                        <option value="" disabled>
                                            {isLoadingChoiceFields ? 'Loading topics...' : 'Select a topic to add'}
                                        </option>
                                        {choiceFields.topics?.map(topic => (
                                            <option 
                                                key={topic} 
                                                value={topic}
                                                disabled={formData.Topics.includes(topic)}
                                            >
                                                {topic}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="topics-list">
                                    {formData.Topics.map((topic, index) => (
                                        <span key={index} className="topic-tag">
                                            <FontAwesomeIcon icon={faTag} />
                                            {topic}
                                            <button
                                                type="button"
                                                onClick={() => handleTopicRemove(topic)}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Links */}
                        <div className="form-section">
                            <h3>Links</h3>
                            
                            <div className="form-group">
                                <label htmlFor="RegistrationLink">Registration Link</label>
                                <input
                                    type="url"
                                    id="RegistrationLink"
                                    name="RegistrationLink"
                                    value={formData.RegistrationLink}
                                    onChange={handleInputChange}
                                    placeholder="https://example.com/register"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="AgendaLink">Agenda Link</label>
                                <input
                                    type="url"
                                    id="AgendaLink"
                                    name="AgendaLink"
                                    value={formData.AgendaLink}
                                    onChange={handleInputChange}
                                    placeholder="https://example.com/agenda"
                                />
                            </div>
                        </div>

                        {/* Social Media */}
                        <div className="form-section">
                            <h3>Social Media</h3>
                            <div className="social-media-section">
                                <div className="platform-buttons">
                                    <div className="platform-button-group">
                                        <button
                                            type="button"
                                            onClick={() => handleGenerateSocial('linkedin')}
                                            className={`btn-platform ${selectedPlatform === 'linkedin' ? 'active' : ''} ${formData.SocialMediaPostLinkedIn ? 'has-content' : ''}`}
                                            disabled={generatingPlatform !== null || !formData.Title}
                                            data-platform="linkedin"
                                            title="Generate LinkedIn post"
                                        >
                                            {generatingPlatform === 'linkedin' ? (
                                                <><FontAwesomeIcon icon={faSpinner} spin /> Generating...</>
                                            ) : (
                                                <><FontAwesomeIcon icon={faLinkedin} /> LinkedIn {getSocialPostStatus('linkedin')}</>
                                            )}
                                        </button>
                                        {formData.SocialMediaPostLinkedIn && (
                                            <button
                                                type="button"
                                                onClick={() => handleClearSocial('linkedin')}
                                                className="btn-clear-social"
                                                title="Clear LinkedIn post"
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        )}
                                    </div>
                                    <div className="platform-button-group">
                                        <button
                                            type="button"
                                            onClick={() => handleGenerateSocial('twitter')}
                                            className={`btn-platform ${selectedPlatform === 'twitter' ? 'active' : ''} ${formData.SocialMediaPostTwitterX ? 'has-content' : ''}`}
                                            disabled={generatingPlatform !== null || !formData.Title}
                                            data-platform="twitter"
                                            title="Generate X/Twitter post"
                                        >
                                            {generatingPlatform === 'twitter' ? (
                                                <><FontAwesomeIcon icon={faSpinner} spin /> Generating...</>
                                            ) : (
                                                <><FontAwesomeIcon icon={faXTwitter} /> X/Twitter {getSocialPostStatus('twitter')}</>
                                            )}
                                        </button>
                                        {formData.SocialMediaPostTwitterX && (
                                            <button
                                                type="button"
                                                onClick={() => handleClearSocial('twitter')}
                                                className="btn-clear-social"
                                                title="Clear X/Twitter post"
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        )}
                                    </div>
                                    <div className="platform-button-group">
                                        <button
                                            type="button"
                                            onClick={() => handleGenerateSocial('facebook')}
                                            className={`btn-platform ${selectedPlatform === 'facebook' ? 'active' : ''} ${formData.SocialMediaPostFacebook ? 'has-content' : ''}`}
                                            disabled={generatingPlatform !== null || !formData.Title}
                                            data-platform="facebook"
                                            title="Generate Facebook post"
                                        >
                                            {generatingPlatform === 'facebook' ? (
                                                <><FontAwesomeIcon icon={faSpinner} spin /> Generating...</>
                                            ) : (
                                                <><FontAwesomeIcon icon={faFacebookSquare} /> Facebook {getSocialPostStatus('facebook')}</>
                                            )}
                                        </button>
                                        {formData.SocialMediaPostFacebook && (
                                            <button
                                                type="button"
                                                onClick={() => handleClearSocial('facebook')}
                                                className="btn-clear-social"
                                                title="Clear Facebook post"
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {!formData.Title && (
                                    <p className="social-help-text">
                                        Enter an event title to generate social media content
                                    </p>
                                )}
                                {clearedPosts.size > 0 && (
                                    <div className="unsaved-changes-warning">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {clearedPosts.size === 1 ? 
                                            `${Array.from(clearedPosts)[0].charAt(0).toUpperCase() + Array.from(clearedPosts)[0].slice(1)} post will be removed` :
                                            `${clearedPosts.size} posts will be removed`
                                        } - Click "Update Event" to save changes
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Media & Collateral */}
                        <div className="form-section">
                            <h3>Media & Collateral</h3>
                            
                            {renderMediaInput(
                                'image',
                                formData.ImageLink,
                                faImage,
                                'Event Image',
                                'Enter image URL or browse gallery for images...'
                            )}

                            {renderMediaInput(
                                'video',
                                formData.VideoCollateral,
                                faVideo,
                                'Video Collateral',
                                'Enter video URL or browse gallery for videos...'
                            )}

                            {renderMediaInput(
                                'audio',
                                formData.AudioCollateral,
                                faMicrophone,
                                'Audio Collateral',
                                'Enter audio URL or browse gallery for audio/podcasts...'
                            )}

                            {renderMediaInput(
                                'document',
                                formData.FileCollateral,
                                faFilePdf,
                                'PDF Document',
                                'Enter PDF URL or browse repository for documents...'
                            )}

                            <div className="form-group">
                                <label htmlFor="CollateralPublishFlag">Publish Status</label>
                                <div className="publish-toggle-container">
                                    <input
                                        type="checkbox"
                                        id="CollateralPublishFlag"
                                        name="CollateralPublishFlag"
                                        checked={formData.CollateralPublishFlag}
                                        onChange={handleInputChange}
                                        className="publish-switch"
                                    />
                                    <label htmlFor="CollateralPublishFlag" className="publish-switch-label">
                                        <span className="publish-switch-slider"></span>
                                    </label>
                                    <span className={`publish-status ${formData.CollateralPublishFlag ? 'published' : 'unpublished'}`}>
                                        {formData.CollateralPublishFlag ? (
                                            <>
                                                <FontAwesomeIcon icon={faVideo} className="status-icon published" />
                                                Publish collateral to members
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faVideo} className="status-icon unpublished" />
                                                Keep collateral private
                                            </>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="form-actions">
                        <button type="button" className="btn-cancel" onClick={onClose}>
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className={`btn-save ${clearedPosts.size > 0 ? 'has-unsaved-changes' : ''}`}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faSave} />
                                    {event ? 'Update Event' : 'Create Event'}
                                    {clearedPosts.size > 0 && <span className="unsaved-badge">!</span>}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* Gallery Browser for Images, Videos, and Audio */}
            {showBrowser.image && (
                <GalleryBrowser
                    galleries={galleries.filter(g => g.type?.toLowerCase() === 'image')}
                    mediaType="image"
                    onSelect={(item) => handleMediaSelect('image', item)}
                    onClose={() => handleBrowserClose('image')}
                    onRefreshGalleries={loadGalleries}
                />
            )}

            {showBrowser.video && (
                <GalleryBrowser
                    galleries={galleries.filter(g => g.type?.toLowerCase() === 'video')}
                    mediaType="video"
                    onSelect={(item) => handleMediaSelect('video', item)}
                    onClose={() => handleBrowserClose('video')}
                    onRefreshGalleries={loadGalleries}
                />
            )}

            {showBrowser.audio && (
                <GalleryBrowser
                    galleries={galleries.filter(g => g.type?.toLowerCase() === 'podcast')}
                    mediaType="podcast"
                    onSelect={(item) => handleMediaSelect('audio', item)}
                    onClose={() => handleBrowserClose('audio')}
                    onRefreshGalleries={loadGalleries}
                />
            )}

            {/* Document Browser for PDFs */}
            {showBrowser.document && (
                <DocumentBrowser
                    documents={documents}
                    documentTypes={[]} // You may want to pass this as props
                    workingGroups={[]} // You may want to pass this as props
                    onSelect={(doc) => handleMediaSelect('document', doc)}
                    onClose={() => handleBrowserClose('document')}
                    onUpload={async (formData) => {
                        // Handle document upload if needed
                        toast.info('Document upload not implemented in events yet');
                        return false;
                    }}
                    onYearChange={() => {}}
                    onRefresh={loadDocuments}
                    isLoading={loadingDocuments}
                    title="Select Document for Event"
                />
            )}

            {/* Social Media Modal */}
            {socialContent && showSocialModal && (
                <div className="modal-overlay">
                    <div className="social-content-modal">
                        <div className="modal-header">
                            <h2>
                                <FontAwesomeIcon icon={faShare} />
                                {selectedPlatform.charAt(0).toUpperCase() + selectedPlatform.slice(1)} Post
                            </h2>
                            <div className="modal-header-buttons">
                                <button
                                    onClick={() => setShowSocialModal(false)}
                                    className="close-button"
                                    type="button"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                        </div>
                        <div className="modal-body">
                            <div className={`social-preview ${selectedPlatform}`}>
                                <div className="platform-icon">
                                    <FontAwesomeIcon
                                        icon={
                                            selectedPlatform === 'linkedin' ? faLinkedin :
                                                selectedPlatform === 'twitter' ? faXTwitter :
                                                    faFacebookSquare
                                        }
                                        size="2x"
                                    />
                                </div>
                                <div className="social-content">
                                    {socialContent.split('\n').map((line, index) => {
                                        // Handle bold text
                                        const formattedLine = line.replace(
                                            /\*\*(.*?)\*\*/g,
                                            '<strong>$1</strong>'
                                        );

                                        return (
                                            <p
                                                key={index}
                                                dangerouslySetInnerHTML={{ __html: formattedLine }}
                                                className={line.trim().startsWith('**') ? 'bold-line' : ''}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <div className="footer-buttons">
                                <button
                                    onClick={() => setShowSocialModal(false)}
                                    className="btn-secondary"
                                    type="button"
                                >
                                    <FontAwesomeIcon icon={faTimes} /> Close
                                </button>
                                <button
                                    onClick={handleRegenerateSocial}
                                    className="btn-secondary btn-regenerate"
                                    disabled={generatingPlatform !== null}
                                    type="button"
                                    title="Regenerate social media content"
                                >
                                    {generatingPlatform === selectedPlatform ? (
                                        <><FontAwesomeIcon icon={faSpinner} spin /> Regenerating...</>
                                    ) : (
                                        <><FontAwesomeIcon icon={faRotate} /> Regenerate</>
                                    )}
                                </button>
                                <button
                                    onClick={(e) => handleCopySocial(e)}
                                    className="btn-secondary"
                                    type="button"
                                >
                                    <FontAwesomeIcon icon={faCopy} /> Copy
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default EventForm; 